import type { Competition, Id, Match, Phase, RuleSet, Team, Tournament } from '../../types.js';
import { effectiveRules } from '../../rules/presets.js';
import { EngineError } from '../state.js';
import { clone } from '../../clone.js';
import { sideOf } from '../../slots.js';

/**
 * Gemeinsames der Aktionsfamilien in `apply`: Nachschlagen mit klarer
 * Fehlermeldung und das feldweise Übernehmen von Patches.
 */

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export function competition(t: Tournament, id: Id): Competition {
  const c = t.competitions.find((x) => x.id === id);
  if (!c) throw new EngineError(`Bewerb ${id} nicht gefunden`);
  return c;
}

export function phaseOf(t: Tournament, phaseId: Id): { comp: Competition; phase: Phase } {
  for (const comp of t.competitions) {
    const phase = comp.phases.find((p) => p.id === phaseId);
    if (phase) return { comp, phase };
  }
  throw new EngineError(`Phase ${phaseId} nicht gefunden`);
}

export function matchOf(t: Tournament, matchId: Id): { comp: Competition; phase: Phase; match: Match } {
  for (const comp of t.competitions) {
    for (const phase of comp.phases) {
      const match = phase.matches.find((m) => m.id === matchId);
      if (match) return { comp, phase, match };
    }
  }
  throw new EngineError(`Spiel ${matchId} nicht gefunden`);
}

export function teamOf(comp: Competition, teamId: Id): Team {
  const team = comp.teams.find((x) => x.id === teamId);
  if (!team) throw new EngineError(`Team ${teamId} nicht gefunden`);
  return team;
}

export function rulesOf(comp: Competition, phase: Phase): RuleSet {
  return effectiveRules(comp.ruleSet, phase.ruleSetOverride);
}

/** Seite des Teams im Spiel — auch als wechselnder Partner (`slots.ts`) */
export const involves = sideOf;

// ---------------------------------------------------------------------------
// Patches
// ---------------------------------------------------------------------------

/*
 * Patches feldweise übernehmen — nie `Object.assign` mit dem rohen Patch.
 *
 * Der Typ beschränkt den Patch auf ein paar Felder, zur Laufzeit ist die
 * Aktion aber JSON vom Server oder aus einer Datei. Ein beschädigtes oder
 * manipuliertes Ereignis könnte mit `Object.assign` `log`, `id` oder
 * `competitions` überschreiben, und das Replay ergäbe hinterher stumm etwas
 * anderes. Deshalb je Aktionstyp eine Liste der erlaubten Felder (bei der
 * jeweiligen Familie).
 */
export function uebernehmen<T extends object>(ziel: T, patch: Partial<T>, felder: readonly (keyof T)[]): void {
  for (const k of felder) {
    // nur eigene Felder — nie etwas, das über den Prototyp hereinkommt
    if (!Object.prototype.hasOwnProperty.call(patch, k)) continue;
    const v = patch[k];
    if (v === undefined) delete ziel[k];
    // Objekte kopieren: der Zustand darf nichts mit der Aktion im Protokoll teilen
    else ziel[k] = (v !== null && typeof v === 'object' ? clone(v) : v) as T[keyof T];
  }
}
